<script setup>
/**
 * FormControlTagsUsageComponent
 * Lists items of a specific component type that use a particular tag.
 * * Note: Optimized for Vue 2.7 Composition API.
 */
import {computed, ref, watch} from 'vue'
import {useRouter} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useItemDataStore} from '@/stores/itemData'
import api from '@/services/api'

const props = defineProps({
    componentId: {
        type: Number,
        required: true
    },
    tagId: {
        type: Number,
        required: true
    },
    itemIds: {
        type: Array,
        default: () => []
    },
})

const router = useRouter()

const componentsStore = useComponentsStore()
const itemDataStore = useItemDataStore()

const items = ref([])

const component = computed(() => {
    return componentsStore.items.find(o => o.id === props.componentId) || {}
})

const tableName = computed(() => component.value.tableName)

const nameField = computed(() => {
    return componentsStore.nameField(props.componentId)
})

watch(() => [props.itemIds, tableName.value], async ([newIds, newTable]) => {
    if (!newIds?.length || !newTable) {
        items.value = []
        return
    }

    try {
        const objs = await itemDataStore.preload({
            tableName: newTable,
            ids: newIds,
        })
        items.value = objs || []
    } catch (error) {
        console.error('Failed to preload usage items:', error)
        items.value = []
    }
}, {immediate: true})

const onClick = (id) => {
    router.push({
        name: 'form',
        params: {
            component: tableName.value,
            id: id
        }
    })
}

const removeTag = async (itemId) => {
    try {
        await api.post('api/tags/remove-tag-from-item', {
            tableName: tableName.value,
            itemId: itemId,
            tagId: props.tagId
        })

        items.value = items.value.filter(item => item.id !== itemId)
    } catch (error) {
        console.error('Failed to remove tag from item:', error)
    }
}
</script>

<template>
    <div v-if="items.length">
        <p class="form-control-tags-usage--heading">
            {{ component.title }}
        </p>

        <div class="list-group">
            <div
                v-for="item in items"
                :key="item.id"
                class="list-group-item form-control-tags-usage--list-group-item"
            >
                <a href="#" @click.prevent="onClick(item.id)">
                    {{ item[nameField] }}
                </a>

                <button class="btn btn-sm" type="button" @click="removeTag(item.id)">
                    <i class="bi-trash3"/>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.form-control-tags-usage--heading {
    margin: 9px 10px;
    font-size: 13px;
    font-weight: 700;
}

.form-control-tags-usage--list-group-item {
    display: flex;
    align-items: center;
}

.form-control-tags-usage--list-group-item > :first-child {
    flex: auto;
}

.bi-trash3 {
    cursor: pointer;
}
</style>